import React, { useCallback, useState } from "react"
import { useFetcher } from "react-router"

import type { ActionResponse } from "@/utils/types"
import type { LoaderData, TimelineConfig as BrandUiTimelineConfig } from "./brand-ui-config"
import { SharedTimelineTab, type TimelineConfig, type IconConfig } from "../shared/SharedTimelineTab"

export function TimelineTab({ config, data }: { config: BrandUiTimelineConfig | undefined; data: LoaderData }) {
  const fetcher = useFetcher<ActionResponse>()

  const [timelineConfig, setTimelineConfig] = useState<TimelineConfig>(() => buildTimelineConfig(config))

  const pendingAction = (fetcher.formData?.get("action") as string | null) ?? null

  const handleSave = useCallback(
    (newConfig: TimelineConfig) => {
      const formData = new FormData()
      formData.append("action", "SAVE_SECTION")
      formData.append("organisation", data.loadedOrganisation)
      formData.append("brand", data.loadedBrand ?? "")
      formData.append("jsonPath", "$.timeline")
      formData.append("value", JSON.stringify(newConfig))
      formData.append("concurrency", data.config?.concurrency ?? "")

      fetcher.submit(formData, { method: "post" })
    },
    [fetcher, data],
  )

  const handleRemove = useCallback(() => {
    const formData = new FormData()
    formData.append("action", "REMOVE_SECTION")
    formData.append("organisation", data.loadedOrganisation)
    formData.append("brand", data.loadedBrand ?? "")
    formData.append("jsonPath", "$.timeline")
    formData.append("concurrency", data.config?.concurrency ?? "")

    fetcher.submit(formData, { method: "post" })
  }, [fetcher, data])

  return (
    <SharedTimelineTab
      timelineConfig={timelineConfig}
      onConfigChange={setTimelineConfig}
      onSave={handleSave}
      onRemove={handleRemove}
      isFetching={fetcher.state !== "idle"}
      currentAction={pendingAction as "SAVE_SECTION" | "REMOVE_SECTION" | null}
    />
  )
}

function buildTimelineConfig(config: BrandUiTimelineConfig | undefined): TimelineConfig {
  const defaultConfig = getDefaultTimelineConfig()
  if (!config) return defaultConfig

  return {
    general: config.general ?? defaultConfig.general,
    upper: {
      allowManualPeriodDateAdjustment: config.upper?.allowManualPeriodDateAdjustment ?? false,
      allowManualPeriodOptionsAdjustment: config.upper?.allowManualPeriodOptionsAdjustment ?? false,
      periodNameSingular: config.upper?.periodNameSingular ?? "",
      periodNamePlural: config.upper?.periodNamePlural ?? "",
      periodOverlapMonths: config.upper?.periodOverlapMonths ?? 0,
      periodOverlapMonthsToEOM: config.upper?.periodOverlapMonthsToEOM ?? false,
      disableOverlapMonths: config.upper?.disableOverlapMonths ?? false,
      periodOptions: config.upper?.periodOptions ?? [],
      icons: {
        autoTag: (config.upper?.icons?.autoTag ?? {}) as Record<string, IconConfig[]>,
        userTag: (config.upper?.icons?.userTag ?? {}) as Record<string, IconConfig[]>,
        evidenceCounts: (config.upper?.icons?.evidenceCounts ?? []) as IconConfig[],
      },
    },
    lower: config.lower ?? defaultConfig.lower,
    instances: config.instances
      ? {
          showSequenceNumber: config.instances.showSequenceNumber,
          instanceStyle: {
            defaultBackground: config.instances.instanceStyle.defaultBackground,
            defaultColour: config.instances.instanceStyle.defaultColour,
            type: config.instances.instanceStyle.type,
            instanceTag: config.instances.instanceStyle.instanceTag,
            values: config.instances.instanceStyle.values ?? [],
          },
          instanceIcons: config.instances.instanceIcons,
        }
      : defaultConfig.instances,
  }
}

function getDefaultTimelineConfig(): TimelineConfig {
  return {
    general: { useUpper: false, useLower: false, useInstances: false },
    upper: {
      allowManualPeriodDateAdjustment: false,
      allowManualPeriodOptionsAdjustment: false,
      periodNameSingular: "",
      periodNamePlural: "",
      periodOverlapMonths: 0,
      periodOverlapMonthsToEOM: false,
      disableOverlapMonths: false,
      periodOptions: [],
      icons: {
        autoTag: {},
        userTag: {},
        evidenceCounts: [],
      },
    },
    lower: { showRecordClusters: false },
    instances: {
      showSequenceNumber: false,
      instanceStyle: {
        defaultBackground: "#ffffff",
        defaultColour: "#000000",
        type: "",
        instanceTag: "",
        values: [],
      },
      instanceIcons: [],
    },
  }
}
